import { observer } from "mobx-react";
import moment from "moment";
import * as React from "react";
import styled from "styled-components";
import { useNavigate, useParams } from "react-router";

import LicensorModal from "@client/components/containers/LicensorModal";
import LicensorsTable from "@client/components/containers/PartnerDetailsPage/LicensorsTable";
import DetailsTemplate from "@client/components/templates/DetailsTemplate";
import AlertModal from "@client/components/ui/AlertModal";
import Button from "@client/components/ui/Button";
import { Action as DropdownActionType } from "@client/components/ui/DropdownAction";
import Table from "@client/components/ui/Table";
import TagGroup from "@client/components/ui/TagGroup";
import partnerStore from "@client/stores/PartnerStore";
import tagStore from "@client/stores/TagStore";
import ExportPdfButton from "@client/utils/ExportPdfButton";
import Style from "@client/utils/Style";
import { LicensorWithRole } from "@typings/Licensor";
import { Partner } from "@typings/Partner";
import { Tag } from "@typings/Tag";

import PartnerModal from "../PartnerModal";
import DetailsFields from "./DetailsFields";
import partnerActionBarImage from "./images/partner-action-bar.svg";

const CLOUDBASE_SOLUTIONS_PARTNER_NAME = "Cloudbase Solutions";

const Wrapper = styled.div`
  height: 100%;
`;

const ExportButtonWrapper = styled.div`
  position: fixed;
  bottom: 20px;
  right: 20px;
  z-index: 1000;
`;

const Content = styled.div``;
const Buttons = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: 52px;
  gap: 24px;
`;

const CenterButton = styled.div`
  justify-self: center;
`;
const CustomerName = styled.div``;
const CustomerNameLabel = styled.div`
  color: ${Style.palette.primary};
`;

type LicensorModalConfig = {
  show: boolean;
  licensor: LicensorWithRole | null;
};

type State = {
  showDeleteAlert: boolean;
  showPartnerModal: boolean;
  showDeleteErrorAlert: boolean;
  selectedNavigationItem: string;
  licensorModal: LicensorModalConfig;
  showLicencesBreakdown: boolean;
};
type Props = {
  onNavigate: (path: string) => void;
  id: string;
};

@observer
class PartnerDetailsPage extends React.Component<Props, State> {
  state: State = {
    showDeleteAlert: false,
    showPartnerModal: false,
    showDeleteErrorAlert: false,
    selectedNavigationItem: "details",
    licensorModal: {
      show: false,
      licensor: null,
    },
    showLicencesBreakdown: false,
  };

  get partner(): Partner | undefined {
    return partnerStore.partners.find(o => o.id === this.props.id);
  }

  get isCloudbaseSolutions(): boolean {
    return this.partner?.name === CLOUDBASE_SOLUTIONS_PARTNER_NAME;
  }

  formatDate(date: string | null | undefined): string {
    return date ? moment.utc(date).format("DD MMM YYYY HH:mm") : "-";
  }

  async componentDidMount() {
    const primaryData = async () =>
      Promise.all([tagStore.load(), partnerStore.load()]);

    const secondaryData = async () =>
      Promise.all([
        partnerStore.loadLicensors(this.partner!.id),
        partnerStore.loadCustomers(this.partner!.id),
        partnerStore.loadLicences(this.partner!.id),
      ]);

    await primaryData();
    await secondaryData();
  }

  componentWillUnmount(): void {
    partnerStore.cancelRequests(this.partner!.id);
    partnerStore.clearLicensors();
    partnerStore.clearCustomers();
    partnerStore.clearLicences();
  }

  getTag(name: string | null): Tag | undefined {
    return tagStore.tags.find(t => t.name === name);
  }

  handleCheckDelete() {
    this.setState({ showDeleteAlert: true });
  }

  async handleDeletePartner() {
    this.setState({ showDeleteAlert: false });
    await partnerStore.delete(this.partner!.id);
    this.props.onNavigate("/partners");
  }

  async handleDeleteLicensor(licensor: LicensorWithRole) {
    await partnerStore.deleteLicensor(licensor);
  }

  async handleDisableLicensor(licensor: LicensorWithRole) {
    await partnerStore.disableLicensor(licensor);
  }

  renderLicencesTable() {
    const partner = this.partner;
    if (!partner) {
      return null;
    }

    const items = partnerStore.customers.map(customer => {
      return [
        <CustomerName key={customer.id}>
          <CustomerNameLabel>{customer.name || customer.id}</CustomerNameLabel>
        </CustomerName>,
        <TagGroup
          key={customer.id}
          licences={partnerStore.licences.filter(
            l => l.customer_id === customer.id,
          )}
          tags={tagStore.tags}
        />,
        moment.utc(customer.created_at).format("DD MMM YYYY HH:mm"),
        customer.created_by,
      ];
    });

    return (
      <Table
        header={["Customers", "Licences", "Created At", "Created By"]}
        noItemsLabel="No customers created by this partner!"
        items={items}
        useSecondaryStyle
        style={{ marginTop: "32px" }}
      />
    );
  }

  renderLicencesBreakdownTable() {
    const partner = this.partner;
    if (!partner) {
      return null;
    }

    const licences = partnerStore.licences;
    if (licences.length === 0) {
      return null;
    }

    const items = licences.map(licence => {
      return [
        licence.name || licence.id,
        licence.tag || "No Tag",
        this.formatDate(licence.created_at),
        `${licence.migrations} / ${licence.replicas}`,
        this.formatDate(licence.start_date),
        this.formatDate(licence.end_date),
        licence.duration || "-",
      ];
    });

    return (
      <Table
        header={[
          "Licences",
          "Tag",
          "Created At",
          "Migrations / Replicas",
          "Start Date",
          "End Date",
          "Duration",
        ]}
        items={items}
        useSecondaryStyle
        style={{ marginTop: "32px" }}
      />
    );
  }

  renderDetailsSubPage() {
    return (
      <Content>
        <DetailsFields
          partner={this.partner || null}
          licences={partnerStore.licences}
          tags={tagStore.tags}
        />
        {this.renderLicencesTable()}
        <Buttons>
          <Button
            primary
            onClick={() => {
              this.setState({ showPartnerModal: true });
            }}
          >
            Edit Partner
          </Button>

          {this.isCloudbaseSolutions ? (
            <CenterButton>
              <Button
                hollow
                onClick={() => {
                  this.setState(prev => ({
                    showLicencesBreakdown: !prev.showLicencesBreakdown,
                  }));
                }}
              >
                {this.state.showLicencesBreakdown ? "Hide" : "Show"} Licences
                Breakdown
              </Button>
            </CenterButton>
          ) : null}

          <Button
            hollow
            onClick={() => {
              this.setState({ licensorModal: { show: true, licensor: null } });
            }}
          >
            Add Licensor
          </Button>
        </Buttons>

        {this.isCloudbaseSolutions &&
          this.state.showLicencesBreakdown &&
          this.renderLicencesBreakdownTable()}
      </Content>
    );
  }

  renderLicensorsSubPage() {
    return (
      <Content>
        <LicensorsTable
          licensors={partnerStore.licensors}
          onDisableLicensor={licensor => {
            this.handleDisableLicensor(licensor);
          }}
          onRemoveLicensor={licensor => {
            this.handleDeleteLicensor(licensor);
          }}
        />
      </Content>
    );
  }

  renderContent() {
    switch (this.state.selectedNavigationItem) {
      case "details":
        return this.renderDetailsSubPage();
      case "licensors":
        return this.renderLicensorsSubPage();
      default:
        return null;
    }
  }

  render() {
    const actions: DropdownActionType[] = [
      {
        label: "Edit Partner",
        color: Style.palette.primary,
        action: () => {
          this.setState({ showPartnerModal: true });
        },
      },
      {
        label: "Add Licensor",
        color: Style.palette.primary,
        action: () => {
          this.setState({ licensorModal: { show: true, licensor: null } });
        },
      },
      {
        label: "Delete Partner",
        color: Style.palette.alert,
        action: () => {
          this.handleCheckDelete();
        },
      },
    ];

    const partner = this.partner;
    if (!partner) {
      return null;
    }

    const pdfColumns = [
      { header: "No.", key: "index" },
      { header: "Customer", key: "customer" },
      { header: "Customer Created At", key: "customer_created_at" },
      { header: "Licence Name", key: "licence_name" },
      { header: "Licence Tag", key: "licence_tag" },
      { header: "Licence Created At", key: "licence_created_at" },
      { header: "Licence Created By", key: "licence_created_by" },
      { header: "Migrations/Replicas", key: "migrations_replicas" },
      { header: "Start Date", key: "start_date" },
      { header: "End Date", key: "end_date" },
      { header: "Duration", key: "duration" },
    ];

    const pdfData: any[] = [];
    let rowIndex = 1;

    partnerStore.customers
      .filter(customer => customer.partner_id === partner.id)
      .forEach(customer => {
        const customerLicences = partnerStore.licences.filter(
          l => l.customer_id === customer.id,
        );
        if (customerLicences.length === 0) {
          pdfData.push({
            index: rowIndex++,
            customer: customer.name || customer.id,
            customer_created_at: this.formatDate(customer.created_at),
            licence_name: "-",
            licence_tag: "-",
            licence_created_at: "-",
            licence_created_by: "-",
            migrations_replicas: "-",
            start_date: "-",
            end_date: "-",
            duration: "-",
          });
        } else {
          customerLicences.forEach(licence => {
            pdfData.push({
              index: rowIndex++,
              customer: customer.name || customer.id,
              customer_created_at: this.formatDate(customer.created_at),
              licence_name: licence.name || licence.id,
              licence_tag: licence.tag || "No Tag",
              licence_created_at: this.formatDate(licence.created_at),
              licence_created_by: licence.created_by || "-",
              migrations_replicas: `${licence.migrations}/${licence.replicas}`,
              start_date: this.formatDate(licence.start_date),
              end_date: this.formatDate(licence.end_date),
              duration: licence.duration ? String(licence.duration) : "-",
            });
          });
        }
      });

    return (
      <Wrapper>
        <DetailsTemplate
          navigationItems={[
            {
              id: "details",
              label: "Details",
            },
            {
              id: "licensors",
              label: "Licensors",
            },
          ]}
          selectedNavigationItem={this.state.selectedNavigationItem}
          onNavigationItemClick={(id: string) => {
            this.setState({ selectedNavigationItem: id });
          }}
          contentComponent={this.renderContent()}
          backLink="/partners"
          dropdownActions={actions}
          icon={partnerActionBarImage}
          title={this.partner?.name || ""}
          subtitle={this.partner?.description || null}
        />
        <ExportButtonWrapper>
          <ExportPdfButton
            data={pdfData}
            title="Partner Details"
            columns={pdfColumns}
          />
        </ExportButtonWrapper>
        {this.state.showDeleteAlert ? (
          <AlertModal
            isOpen
            title="Delete Partner"
            message="Are you sure you want to delete the partner?"
            extraMessage="Deleting a Coriolis Partner is permanent!"
            onConfirmation={() => {
              this.handleDeletePartner();
            }}
            onRequestClose={() => {
              this.setState({ showDeleteAlert: false });
            }}
          />
        ) : null}
        {this.state.showPartnerModal ? (
          <PartnerModal
            onRequestClose={() => {
              this.setState({ showPartnerModal: false });
            }}
            partner={this.partner}
          />
        ) : null}
        {this.state.showDeleteErrorAlert ? (
          <AlertModal
            isOpen
            type="error"
            title="Cannot Delete Partner"
            message="The partner has licences associated with him"
            extraMessage="You must first delete the associated licences"
            onRequestClose={() => {
              this.setState({ showDeleteErrorAlert: false });
            }}
          />
        ) : null}
        {this.state.licensorModal.show ? (
          <LicensorModal
            onRequestClose={async () => {
              this.setState({ licensorModal: { show: false, licensor: null } });
              await partnerStore.loadLicensors(this.partner!.id);
            }}
            licensor={this.state.licensorModal.licensor}
            partner={this.partner!}
          />
        ) : null}
      </Wrapper>
    );
  }
}

function PartnerDetailsPageWithNavigate() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  return <PartnerDetailsPage onNavigate={navigate} id={id!} />;
}

export default PartnerDetailsPageWithNavigate;
